// Formato y aritmética de fechas de cita en hora de Colombia.
//
// Vivían como funciones locales de DashboardPatient; se extraen porque el modal
// de reagendar necesita exactamente las mismas conversiones. Duplicarlas sería
// pedir que un día el selector de reagendar y el de agendar interpreten la
// misma hora de forma distinta.

export const APPOINTMENT_TIME_ZONE = 'America/Bogota';
// Colombia no tiene horario de verano: el offset -05:00 es fijo todo el año.
export const BOGOTA_UTC_OFFSET = '-05:00';

export const formatAppointmentDate = (dateValue) => {
  if (!dateValue) return '';

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return parsedDate.toLocaleDateString('es-CO', {
    timeZone: APPOINTMENT_TIME_ZONE,
  });
};

export const formatAppointmentTime = (dateValue) => {
  if (!dateValue) return '';

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return parsedDate.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: APPOINTMENT_TIME_ZONE,
  });
};

// Devuelve la fecha de hoy en Bogotá (GMT-5) como string YYYY-MM-DD,
// para usarla de valor mínimo del selector de fecha.
export const getTodayInBogota = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APPOINTMENT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const d = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${d}`;
};

// Un día 'YYYY-MM-DD' como Date a mediodía UTC. Para operar con días o
// formatearlos (con timeZone 'UTC') sin que la zona del navegador los corra al
// día anterior.
export const dateStrAtNoon = (dateStr) => new Date(`${dateStr}T12:00:00Z`);

// 'YYYY-MM-DD' + n días.
export const addDaysToDateStr = (dateStr, n) => {
  const d = dateStrAtNoon(dateStr);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// Lunes de la semana en curso en Bogotá, como YYYY-MM-DD.
export const getWeekStartInBogota = () => {
  const today = getTodayInBogota();
  const weekday = dateStrAtNoon(today).getUTCDay(); // 0 = domingo
  return addDaysToDateStr(today, -((weekday + 6) % 7));
};

// Convierte HH:MM (hora local de Bogotá) + fecha YYYY-MM-DD en un Date UTC
// correcto, fijando el offset -05:00 explícitamente.
export const slotToDate = (dateStr, time) => new Date(`${dateStr}T${time}:00${BOGOTA_UTC_OFFSET}`);

// ── Round-trip para <input type="datetime-local"> ──────────────────────────
//
// Un datetime-local no tiene zona: es hora de pared. Las dos conversiones DEBEN
// usar la misma zona o el valor se desplaza en cada guardado.
//
// El modal de editar cita del panel de admin las tenía descuadradas: rellenaba
// con `new Date(iso).toISOString().slice(0,16)` — que es UTC pese a llamarse
// `toLocal` — y guardaba con `new Date(valor).toISOString()`, que interpreta la
// hora de pared como LOCAL. El resultado era que cada guardado corría la cita
// +5 horas, aunque el admin solo hubiera cambiado el estado y no tocara la fecha.

/** Date/ISO → 'YYYY-MM-DDTHH:mm' en hora de Bogotá, para rellenar el input. */
export const toDateTimeLocalInput = (dateValue) => {
  if (!dateValue) return '';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APPOINTMENT_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(parsed);
  const get = (type) => parts.find((p) => p.type === type).value;
  // en-CA con hour12:false puede dar '24' para la medianoche según el motor.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
};

/** 'YYYY-MM-DDTHH:mm' (hora de Bogotá) → ISO UTC, para enviar a la API. */
export const fromDateTimeLocalInput = (value) => {
  if (typeof value !== 'string') return undefined;
  // Se valida la FORMA antes de parsear: `new Date('no-es-una-fecha:00-05:00')`
  // no lanza ni devuelve Invalid Date, devuelve el 1 de enero de 2000. Sin esta
  // guarda, un valor corrupto reagendaba la cita al año 2000 en silencio.
  const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (!match) return undefined;

  const date = new Date(`${match[1]}T${match[2]}:00${BOGOTA_UTC_OFFSET}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

export const formatSlotLabel = (time) => {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
};
